import { useState, useEffect } from 'react';
import { prescriptionAPI } from '../../lib/api';
import { 
  Pill, 
  ArrowClockwise,
  CheckCircle,
  XCircle,
  Clock,
  Storefront,
  Warning,
  PaperPlaneTilt,
  CaretDown,
  CaretUp
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function PatientRefillsPage() {
  const [medications, setMedications] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(null);
  const [showRequests, setShowRequests] = useState(true);
  const [refillNotes, setRefillNotes] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [medsRes, requestsRes] = await Promise.all([
        prescriptionAPI.getMyMedications(),
        prescriptionAPI.getMyRefillRequests()
      ]);
      setMedications(medsRes.data.medications || []);
      setMyRequests(requestsRes.data.requests || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load medications');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestRefill = async (medicationId) => {
    setSubmitting(medicationId);
    try {
      await prescriptionAPI.requestRefill({
        prescription_id: medicationId,
        notes: refillNotes[medicationId] || null
      });
      toast.success('Refill request submitted');
      setRefillNotes({ ...refillNotes, [medicationId]: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit refill request');
    } finally {
      setSubmitting(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full flex items-center gap-1"><Clock size={12} /> Pending</span>;
      case 'approved':
        return <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full flex items-center gap-1"><CheckCircle size={12} /> Approved</span>;
      case 'denied':
        return <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full flex items-center gap-1"><XCircle size={12} /> Denied</span>;
      case 'completed':
        return <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full flex items-center gap-1"><CheckCircle size={12} /> Completed</span>;
      default:
        return null;
    }
  };

  const hasPendingRequest = (medicationId) => {
    return myRequests.some(r => r.prescription_id === medicationId && r.status === 'pending');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">My Medications</h1>
        <p className="text-slate-500">Request refills for your active prescriptions</p>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Pill size={20} className="text-blue-600 mt-0.5" />
          <div>
            <p className="font-medium text-blue-800">How Refill Requests Work</p>
            <p className="text-sm text-blue-700 mt-1">
              Submit a refill request and our medical staff will review it. Once approved, 
              your prescription will be sent to your pharmacy. You'll see the status update here.
            </p>
          </div>
        </div>
      </div>

      {/* Active Medications */}
      <div className="card-clinical overflow-hidden">
        <div className="p-4 border-b border-slate-200 bg-teal-50">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <Pill size={18} className="text-teal-600" />
            Active Medications ({medications.length})
          </h3>
        </div>

        {medications.length === 0 ? (
          <div className="p-8 text-center">
            <Pill size={48} className="text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No active medications on file</p>
            <p className="text-sm text-slate-400 mt-1">Your prescriptions will appear here once added by your provider</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {medications.map((med) => {
              const isPending = hasPendingRequest(med.id);
              const canRefill = med.can_request_refill && !isPending;
              
              return (
                <div key={med.id} className="p-4" data-testid={`medication-${med.id}`}>
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-semibold text-lg text-slate-900">{med.medication_name}</h4>
                        {isPending && (
                          <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">Refill Pending</span>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                        <div>
                          <span className="text-slate-500">Dosage:</span>
                          <span className="ml-2 font-medium">{med.dosage}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Frequency:</span>
                          <span className="ml-2 font-medium">{med.frequency}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Refills Left:</span>
                          <span className={`ml-2 font-medium ${med.refills_remaining === 0 ? 'text-red-600' : 'text-green-600'}`}>
                            {med.refills_remaining}
                          </span>
                        </div>
                      </div>
                      
                      {med.pharmacy_name && (
                        <div className="mt-2 text-sm text-slate-500 flex items-center gap-1">
                          <Storefront size={14} />
                          Pharmacy: {med.pharmacy_name}
                        </div>
                      )}
                      
                      {med.refills_remaining === 0 && (
                        <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                          <div className="flex items-start gap-2">
                            <Warning size={16} className="text-amber-600 mt-0.5" />
                            <p className="text-sm text-amber-800">
                              No refills remaining. Please contact your provider for a new prescription.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Refill Request Section */}
                    <div className="md:w-64">
                      {canRefill ? (
                        <div className="space-y-2">
                          <textarea
                            placeholder="Optional notes (e.g., need sooner, pharmacy change)"
                            value={refillNotes[med.id] || ''}
                            onChange={(e) => setRefillNotes({ ...refillNotes, [med.id]: e.target.value })}
                            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none focus:ring-2 focus:ring-teal-500"
                            rows={2}
                          />
                          <button
                            onClick={() => handleRequestRefill(med.id)}
                            disabled={submitting === med.id}
                            className="w-full btn-teal flex items-center justify-center gap-2 disabled:opacity-50"
                          >
                            {submitting === med.id ? (
                              <>
                                <div className="spinner w-4 h-4 border-white" />
                                Submitting...
                              </>
                            ) : (
                              <>
                                <ArrowClockwise size={18} />
                                Request Refill
                              </>
                            )}
                          </button>
                        </div>
                      ) : isPending ? (
                        <div className="text-center p-3 bg-amber-50 rounded-lg">
                          <Clock size={24} className="text-amber-500 mx-auto mb-2" />
                          <p className="text-sm text-amber-700 font-medium">Refill request pending</p>
                          <p className="text-xs text-amber-600">We'll notify you when reviewed</p>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* My Refill Requests History */}
      <div className="card-clinical overflow-hidden">
        <div 
          className="p-4 border-b border-slate-200 bg-slate-50 cursor-pointer flex items-center justify-between"
          onClick={() => setShowRequests(!showRequests)}
        >
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <ArrowClockwise size={18} className="text-slate-600" />
            My Refill Requests ({myRequests.length})
          </h3>
          {showRequests ? <CaretUp size={20} /> : <CaretDown size={20} />}
        </div>

        {showRequests && (
          myRequests.length === 0 ? (
            <div className="p-8 text-center">
              <ArrowClockwise size={48} className="text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No refill requests yet</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {myRequests.map((request) => (
                <div key={request.id} className="p-4 hover:bg-slate-50">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium text-slate-900">{request.medication_name}</p>
                        {getStatusBadge(request.status)}
                      </div>
                      <p className="text-sm text-slate-500">{request.dosage}</p>
                      <p className="text-xs text-slate-400 mt-1">
                        Requested: {new Date(request.requested_at).toLocaleDateString()}
                      </p>
                      {request.reviewed_at && (
                        <p className="text-xs text-slate-400">
                          Reviewed: {new Date(request.reviewed_at).toLocaleDateString()} by {request.reviewed_by_name}
                        </p>
                      )}
                      {request.denial_reason && (
                        <p className="text-sm text-red-600 mt-2">
                          Reason: {request.denial_reason}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
}
